'use client';

import React, { createContext, useContext } from 'react';
import { useSession, signIn, signOut } from 'next-auth/react';
import { normalizePlanSlug, NormalizedPlanSlug } from '@/lib/planLimits';
import {
  getConfiguredYandexHelperUrl,
  getYandexLocalConfigError,
  isLoopbackOrigin,
  isYandexLocalHelperConfigured,
  requestLocalYandexAccessToken,
  resolveOrigin,
  YANDEX_LOCAL_PROVIDER_ID,
  type YandexLocalIntent,
} from '@/lib/yandexLocalAuth';

export interface User {
  id: string;
  name: string;
  email: string;
  plan?: NormalizedPlanSlug;
  isAdmin: boolean;
  image?: string | null;
  provider?: 'credentials' | 'yandex';
  pendingEmail?: string | null;
  emailVerificationToken?: string | null;
  emailTokenExpiry?: Date | null;
}

interface SocialLoginOptions {
  provider: 'yandex';
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  yandexAuthEnabled: boolean;
  yandexAuthEntrypointEnabled: boolean;
  login: (callbackUrl?: string) => Promise<void>;
  logout: () => void;
  socialLogin: (options: SocialLoginOptions) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const authContextDeps = {
  useSession,
  signIn,
  signOut,
  fetchSession: () => fetch('/api/auth/session'),
  getConfiguredAppUrl: () => process.env.NEXT_PUBLIC_APP_URL ?? null,
  getCurrentOrigin: () => (typeof window !== 'undefined' ? window.location.origin : null),
  getConfiguredYandexHelperUrl,
  startLocalYandexAuth: requestLocalYandexAccessToken,
  navigateTo: (url: string) => {
    if (typeof window !== 'undefined') {
      window.location.assign(url);
    }
  },
};

function getEffectiveOrigin(currentOrigin: string | null, configuredAppUrl: string | null): string | null {
  return resolveOrigin(currentOrigin) ?? resolveOrigin(configuredAppUrl);
}

function isLocalYandexOrigin(origin: string | null): boolean {
  return isLoopbackOrigin(origin);
}

function getUnavailableYandexMessage(isLocalOrigin: boolean, helperUrl: string | null): string {
  if (isLocalOrigin) {
    return getYandexLocalConfigError(helperUrl);
  }

  return 'Вход через Yandex сейчас недоступен.';
}

export function AuthProvider({
  children,
  yandexAuthEnabled,
  yandexClientId,
}: {
  children: React.ReactNode;
  yandexAuthEnabled: boolean;
  yandexClientId: string | null;
}) {
  const { data: session, status } = authContextDeps.useSession();
  const configuredAppUrl = authContextDeps.getConfiguredAppUrl();
  const currentOrigin = authContextDeps.getCurrentOrigin();
  const helperUrl = authContextDeps.getConfiguredYandexHelperUrl();
  const effectiveOrigin = getEffectiveOrigin(currentOrigin, configuredAppUrl);
  const isLocalOrigin = isLocalYandexOrigin(effectiveOrigin);
  const localYandexReady = Boolean(yandexClientId?.trim()) && isYandexLocalHelperConfigured(helperUrl);
  const yandexAuthEntrypointEnabled = yandexAuthEnabled && (!isLocalOrigin || localYandexReady);
  const yandexAuthErrorMessage = getUnavailableYandexMessage(isLocalOrigin, helperUrl);

  const user = session?.user
    ? {
        id: session.user.id as string,
        name: session.user.name || '',
        email: session.user.email || '',
        image: session.user.image,
        isAdmin: Boolean(session.user.isAdmin),
        plan: normalizePlanSlug(session.user.plan as string | null | undefined) ?? undefined,
        provider: (session.user.provider as 'credentials' | 'yandex') || 'credentials',
      }
    : null;

  const completeLocalYandexAuth = async (
    intent: YandexLocalIntent,
    callbackUrl: string,
  ) => {
    if (!effectiveOrigin || !isLocalOrigin) {
      throw new Error('Локальный вход через Yandex доступен только на localhost.');
    }

    if (!yandexClientId?.trim() || !helperUrl) {
      throw new Error(yandexAuthErrorMessage);
    }

    const { accessToken } = await authContextDeps.startLocalYandexAuth({
      clientId: yandexClientId,
      helperUrl,
      origin: effectiveOrigin,
      intent,
      callbackUrl,
    });

    const result = await authContextDeps.signIn(YANDEX_LOCAL_PROVIDER_ID, {
      redirect: false,
      accessToken,
      intent,
      callbackUrl,
    });

    if (result?.error) {
      throw new Error('Не удалось завершить вход через Yandex.');
    }

    return result?.url ?? callbackUrl;
  };

  const login = async (callbackUrl = '/dashboard') => {
    if (!yandexAuthEntrypointEnabled) {
      throw new Error(yandexAuthErrorMessage);
    }

    if (isLocalOrigin) {
      const redirectUrl = await completeLocalYandexAuth('login', callbackUrl);
      authContextDeps.navigateTo(redirectUrl);
      return;
    }

    await authContextDeps.signIn('yandex', { callbackUrl });
  };

  const logout = async () => {
    await authContextDeps.signOut({ callbackUrl: '/' });
  };

  const socialLogin = async ({ provider, onSuccess, onError }: SocialLoginOptions) => {
    try {
      if (provider !== 'yandex') {
        throw new Error('Неподдерживаемый провайдер входа.');
      }

      if (!yandexAuthEntrypointEnabled) {
        throw new Error(yandexAuthErrorMessage);
      }

      if (isLocalOrigin) {
        await completeLocalYandexAuth('link', '/profile');
        onSuccess?.();
        return;
      }

      const result = await authContextDeps.signIn(provider, {
        redirect: false,
        callbackUrl: '/profile',
      });

      if (result?.error) {
        onError?.(new Error(result.error));
        return;
      }

      if (result?.ok) {
        onSuccess?.();
        return;
      }

      const sessionResponse = await authContextDeps.fetchSession();
      const sessionData = await sessionResponse.json();

      if (sessionData?.user) {
        onSuccess?.();
        return;
      }

      onError?.(new Error('Failed to link account. Please try again.'));
    } catch (error) {
      onError?.(error instanceof Error ? error : new Error('An unknown error occurred'));
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading: status === 'loading',
        yandexAuthEnabled,
        yandexAuthEntrypointEnabled,
        login,
        logout,
        socialLogin,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
